import { useCallback, useMemo, useState, type FC, type KeyboardEvent, type MouseEvent, type ChangeEvent } from "react";
import { AutoComplete, Button, Input } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { LocationTabState } from "src/hooks/UseLocationTab";
import { buildSidebarSections } from "src/layouts/HomePage/Config/SidebarConfig";
import {
  buildSearchEntries,
  filterSearchEntries,
  type SearchEntry,
} from "src/layouts/HomePage/Config/SearchConfig";
import { useDashboardSidebarItems } from "src/layouts/HomePage/Hooks/UseDashboardSidebarItems";
import "./SmartSearch.scss";

type SearchTriggerEvent =
  | ChangeEvent<HTMLInputElement>
  | MouseEvent<HTMLElement>
  | KeyboardEvent<HTMLInputElement>;

const SmartSearch: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dashboardItems = useDashboardSidebarItems();
  const [query, setQuery] = useState("");

  const searchEntries = useMemo(
    () => buildSearchEntries(t, buildSidebarSections(t, dashboardItems)),
    [t, dashboardItems]
  );

  const matchedEntries = useMemo(
    () => filterSearchEntries(searchEntries, query),
    [searchEntries, query]
  );

  const options = useMemo(
    () =>
      matchedEntries.map(entry => ({
        value: entry.id,
        label: (
          <div className="smart-search__option">
            <span className="smart-search__option-label">{entry.label}</span>
            {entry.context ? (
              <span className="smart-search__option-context">{entry.context}</span>
            ) : null}
          </div>
        ),
      })),
    [matchedEntries]
  );

  const navigateToEntry = useCallback(
    (entry: SearchEntry) => {
      const state: LocationTabState | undefined = entry.tabId
        ? { activeTab: entry.tabId }
        : undefined;

      navigate(entry.route, { state });
      setQuery("");
    },
    [navigate]
  );

  const handleSelect = useCallback(
    (entryId: string) => {
      const selectedEntry = matchedEntries.find(entry => entry.id === entryId);
      if (selectedEntry) {
        navigateToEntry(selectedEntry);
      }
    },
    [matchedEntries, navigateToEntry]
  );

  const handleSearchSubmit = useCallback(
    (_value: string, event?: SearchTriggerEvent) => {
      // Enter is owned by AutoComplete (selects the highlighted suggestion via onSelect).
      if (event?.type === "keydown") {
        return;
      }

      const [topEntry] = matchedEntries;
      if (topEntry) {
        navigateToEntry(topEntry);
      }
    },
    [matchedEntries, navigateToEntry]
  );

  const hasQuery = query.trim().length > 0;

  return (
    <AutoComplete
      className="smart-search"
      popupClassName="smart-search__dropdown"
      options={options}
      value={query}
      onSearch={setQuery}
      onSelect={handleSelect}
      notFoundContent={hasQuery ? t("sidebar.searchNoResults") : null}
    >
      <Input.Search
        size="large"
        placeholder={t("sidebar.searchPlaceholder")}
        aria-label={t("sidebar.searchPlaceholder")}
        onSearch={handleSearchSubmit}
        enterButton={
          <Button
            type="primary"
            icon={<SearchOutlined />}
            aria-label={t("sidebar.searchButtonLabel")}
          />
        }
      />
    </AutoComplete>
  );
};

export default SmartSearch;
